import {
  pgTable,
  text,
  varchar,
  timestamp,
  integer,
  boolean,
  jsonb,
  index,
  serial,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// GA Profile — the General Agency entity
export const gaProfiles = pgTable("ga_profiles", {
  id: varchar("id").primaryKey(),
  name: text("name").notNull(),
  npn: varchar("npn", { length: 50 }),
  fein: varchar("fein", { length: 20 }),
  domicileState: varchar("domicile_state", { length: 2 }),
  contactName: varchar("contact_name", { length: 255 }),
  contactEmail: varchar("contact_email", { length: 255 }),
  contactPhone: varchar("contact_phone", { length: 20 }),
  status: varchar("status", { enum: ["active", "suspended"] }).notNull().default("active"),
  createdAt: timestamp("created_at").defaultNow(),
  // Extended profile fields
  logoObjectPath: text("logo_object_path"),
  description: text("description"),
  website: varchar("website", { length: 500 }),
  address: varchar("address", { length: 500 }),
  city: varchar("city", { length: 255 }),
  zip: varchar("zip", { length: 20 }),
  licensedStates: text("licensed_states").array(),
  linkedinUrl: varchar("linkedin_url", { length: 500 }),
  phoneMain: varchar("phone_main", { length: 20 }),
  phoneFax: varchar("phone_fax", { length: 20 }),
});

// GA Users — GA staff members (ga_admin or ga_service_rep)
export const gaUsers = pgTable("ga_users", {
  id: varchar("id").primaryKey(),
  gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(), // FK → users.id
  role: varchar("role", { enum: [
    "ga_admin",
    "ga_service_rep",
    "ga_inside_sales",
    "ga_outside_sales",
    "ga_underwriter",
    "ga_account_manager",
    "ga_operations",
    "ga_compliance",
    "ga_marketing",
  ] }).notNull().default("ga_service_rep"),
  invitedAt: timestamp("invited_at").defaultNow(),
  acceptedAt: timestamp("accepted_at"),
  status: varchar("status", { enum: ["active", "invited", "deactivated"] }).notNull().default("invited"),
  // Extended profile fields
  firstName: varchar("first_name", { length: 255 }),
  lastName: varchar("last_name", { length: 255 }),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 20 }),
  title: varchar("title", { length: 255 }),
  department: varchar("department", { length: 255 }),
  locationId: varchar("location_id", { length: 36 }),
  assignedStates: text("assigned_states").array(),
  notes: text("notes"),
  avatarObjectPath: text("avatar_object_path"),
});

// GA License Blocks — purchased blocks of broker seats
export const gaLicenseBlocks = pgTable("ga_license_blocks", {
  id: varchar("id").primaryKey(),
  gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
  totalLicenses: integer("total_licenses").notNull().default(0),
  purchasedAt: timestamp("purchased_at").defaultNow(),
  billingRef: varchar("billing_ref", { length: 255 }),
  notes: text("notes"),
});

// GA Broker Links — license assignments to broker accounts
export const gaBrokerLinks = pgTable(
  "ga_broker_links",
  {
    id: varchar("id").primaryKey(),
    gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
    brokerAccountId: varchar("broker_account_id").notNull(), // FK → agencies.id
    licenseBlockId: varchar("license_block_id").references(() => gaLicenseBlocks.id),
    linkedAt: timestamp("linked_at").defaultNow(),
    linkedBy: varchar("linked_by"), // FK → ga_users.id
    status: varchar("status", {
      enum: ["pending", "active", "license_revoked"],
    }).notNull().default("pending"),
    // Broker account metadata cached for display
    brokerAgencyName: varchar("broker_agency_name", { length: 255 }),
    brokerContactEmail: varchar("broker_contact_email", { length: 255 }),
    brokerNpn: varchar("broker_npn", { length: 50 }),
    // Funding
    gaFunded: boolean("ga_funded").default(true),
  },
  (table) => [
    index("ga_broker_links_ga_id_idx").on(table.gaId),
    index("ga_broker_links_broker_id_idx").on(table.brokerAccountId),
  ]
);

// GA Build Access — broker grants GA permission to create/configure on their account
export const gaBuildAccess = pgTable("ga_build_access", {
  id: varchar("id").primaryKey(),
  gaBrokerLinkId: varchar("ga_broker_link_id").notNull().references(() => gaBrokerLinks.id, { onDelete: "cascade" }),
  grantedAt: timestamp("granted_at").defaultNow(),
  grantedBy: varchar("granted_by").notNull(), // FK → users.id (broker user)
  expiresAt: timestamp("expires_at"),
  revokedAt: timestamp("revoked_at"),
  revokedBy: varchar("revoked_by"), // FK → users.id
  status: varchar("status", { enum: ["active", "expired", "revoked"] }).notNull().default("active"),
});

// GA Client Visibility — broker shares specific clients with GA
export const gaClientVisibility = pgTable(
  "ga_client_visibility",
  {
    id: varchar("id").primaryKey(),
    gaBrokerLinkId: varchar("ga_broker_link_id").notNull().references(() => gaBrokerLinks.id, { onDelete: "cascade" }),
    clientId: integer("client_id").notNull(), // FK → clients.id
    sharedAt: timestamp("shared_at").defaultNow(),
    sharedBy: varchar("shared_by").notNull(), // FK → users.id (broker user)
    scope: varchar("scope", { enum: ["view_only", "assist"] }).notNull().default("view_only"),
    revokedAt: timestamp("revoked_at"),
    revokedBy: varchar("revoked_by"), // FK → users.id
    status: varchar("status", { enum: ["active", "revoked"] }).notNull().default("active"),
  },
  (table) => [
    index("ga_client_vis_link_idx").on(table.gaBrokerLinkId),
    index("ga_client_vis_client_idx").on(table.clientId),
  ]
);

// GA Audit Log — immutable record of all GA actions
export const gaAuditLogs = pgTable(
  "ga_audit_logs",
  {
    id: varchar("id").primaryKey(),
    gaId: varchar("ga_id").notNull().references(() => gaProfiles.id),
    gaUserId: varchar("ga_user_id").notNull(), // FK → ga_users.id
    brokerAccountId: varchar("broker_account_id"), // FK → agencies.id
    clientId: integer("client_id"), // FK → clients.id
    action: text("action").notNull(),
    recordType: varchar("record_type", { length: 100 }),
    recordId: varchar("record_id"),
    metadata: jsonb("metadata"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("ga_audit_log_ga_id_idx").on(table.gaId),
    index("ga_audit_log_broker_idx").on(table.brokerAccountId),
    index("ga_audit_log_created_idx").on(table.createdAt),
  ]
);

// GA Assisted Tags — stamps on records modified by GA during service sessions
export const gaAssistedTags = pgTable("ga_assisted_tags", {
  id: varchar("id").primaryKey(),
  gaId: varchar("ga_id").notNull().references(() => gaProfiles.id),
  gaUserId: varchar("ga_user_id").notNull(), // FK → ga_users.id
  recordType: varchar("record_type", { length: 100 }).notNull(),
  recordId: varchar("record_id").notNull(),
  action: varchar("action", { enum: ["created", "modified"] }).notNull(),
  timestamp: timestamp("timestamp").defaultNow(),
});

// GA API Connections — underwriting API connections (W&B etc.)
export const gaApiConnections = pgTable("ga_api_connections", {
  id: varchar("id").primaryKey(),
  gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  apiType: varchar("api_type", { length: 100 }).notNull(), // e.g., "word_brown", "sunlife", etc.
  status: varchar("status", { enum: ["connected", "error", "disconnected"] }).notNull().default("disconnected"),
  credentialsMasked: jsonb("credentials_masked"), // masked display of creds
  credentialsEncrypted: text("credentials_encrypted"), // encrypted actual creds
  lastSuccessfulCall: timestamp("last_successful_call"),
  lastError: text("last_error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Broker Engagement Snapshots — computed daily for analytics
export const brokerEngagementSnapshots = pgTable(
  "broker_engagement_snapshots",
  {
    id: varchar("id").primaryKey(),
    brokerAccountId: varchar("broker_account_id").notNull(),
    gaBrokerLinkId: varchar("ga_broker_link_id").references(() => gaBrokerLinks.id),
    snapshotDate: varchar("snapshot_date", { length: 10 }).notNull(), // YYYY-MM-DD
    loginsLast7d: integer("logins_last_7d").default(0),
    loginsLast30d: integer("logins_last_30d").default(0),
    loginsLast90d: integer("logins_last_90d").default(0),
    lastLoginAt: timestamp("last_login_at"),
    employersTotal: integer("employers_total").default(0),
    employersActive: integer("employers_active").default(0),
    employeesTotal: integer("employees_total").default(0),
    employeesEnrolled: integer("employees_enrolled").default(0),
    enrollmentCompletionPct: varchar("enrollment_completion_pct", { length: 10 }).default("0"),
    quotesRunLast30d: integer("quotes_run_last_30d").default(0),
    rfpsSubmittedLast30d: integer("rfps_submitted_last_30d").default(0),
    acaFeaturesUsed: boolean("aca_features_used").default(false),
    hrisFeaturesUsed: boolean("hris_features_used").default(false),
    carrierConnectionsCount: integer("carrier_connections_count").default(0),
    censusCopletednessPct: varchar("census_completeness_pct", { length: 10 }).default("0"),
    adoptionScore: integer("adoption_score").default(0),
    adoptionBand: varchar("adoption_band", { enum: ["building", "active", "power_user"] }).default("building"),
    scoreComputedAt: timestamp("score_computed_at").defaultNow(),
  },
  (table) => [
    index("broker_engagement_broker_idx").on(table.brokerAccountId),
    index("broker_engagement_date_idx").on(table.snapshotDate),
  ]
);

// GA Network Snapshots — daily aggregate across all broker links for a GA
export const gaNetworkSnapshots = pgTable(
  "ga_network_snapshots",
  {
    id: varchar("id").primaryKey(),
    gaId: varchar("ga_id").notNull().references(() => gaProfiles.id),
    snapshotDate: varchar("snapshot_date", { length: 10 }).notNull(),
    totalBrokers: integer("total_brokers").default(0),
    brokersActive30d: integer("brokers_active_30d").default(0),
    brokersInactive30d: integer("brokers_inactive_30d").default(0),
    brokersInactive60d: integer("brokers_inactive_60d").default(0),
    brokersNeverLoggedIn: integer("brokers_never_logged_in").default(0),
    avgAdoptionScore: varchar("avg_adoption_score", { length: 10 }).default("0"),
    brokersBuilding: integer("brokers_building").default(0),
    brokersActive: integer("brokers_active").default(0),
    brokersPowerUser: integer("brokers_power_user").default(0),
    totalEmployers: integer("total_employers").default(0),
    totalEmployees: integer("total_employees").default(0),
    totalEnrolled: integer("total_enrolled").default(0),
    networkEnrollmentPct: varchar("network_enrollment_pct", { length: 10 }).default("0"),
    quotesRunLast30d: integer("quotes_run_last_30d").default(0),
    licensesAssigned: integer("licenses_assigned").default(0),
    licensesAvailable: integer("licenses_available").default(0),
    licensesSelfFunded: integer("licenses_self_funded").default(0),
  },
  (table) => [
    index("ga_network_snapshot_ga_idx").on(table.gaId),
    index("ga_network_snapshot_date_idx").on(table.snapshotDate),
  ]
);

// GA Submission Routing — where case submissions from brokers get distributed
export const gaSubmissionRouting = pgTable("ga_submission_routing", {
  id: varchar("id").primaryKey(),
  gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
  underwrtingEmails: text("underwriting_emails").array().default([]),
  defaultCcEmails: text("default_cc_emails").array().default([]),
  replyToEmail: varchar("reply_to_email", { length: 255 }),
  notifyOnSubmit: boolean("notify_on_submit").default(true),
  notifyOnUpdate: boolean("notify_on_update").default(false),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// GA Broker Assignments — which GA sales rep is assigned to each broker
export const gaBrokerAssignments = pgTable(
  "ga_broker_assignments",
  {
    id: varchar("id").primaryKey(),
    gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
    brokerAccountId: varchar("broker_account_id").notNull(),
    salesRepName: varchar("sales_rep_name", { length: 255 }),
    salesRepEmail: varchar("sales_rep_email", { length: 255 }),
    notes: text("notes"),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("ga_broker_assignments_ga_id_idx").on(table.gaId),
    index("ga_broker_assignments_broker_idx").on(table.brokerAccountId),
  ]
);

// GA Case Submissions — cases submitted by brokers to the GA's underwriting team
export const gaCaseSubmissions = pgTable(
  "ga_case_submissions",
  {
    id: varchar("id").primaryKey(),
    gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
    brokerAccountId: varchar("broker_account_id").notNull(),
    brokerAgencyName: varchar("broker_agency_name", { length: 255 }),
    subject: varchar("subject", { length: 500 }).notNull(),
    notes: text("notes"),
    // Expanded statuses: new | in_review | pending_info | approved | declined | closed
    status: varchar("status", { length: 50 }).default("new"),
    assignedTo: varchar("assigned_to", { length: 255 }),
    uwNotes: text("uw_notes"),
    checklistData: jsonb("checklist_data").default({}),
    participationData: jsonb("participation_data").default({}),
    clientId: integer("client_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("ga_case_submissions_ga_id_idx").on(table.gaId),
    index("ga_case_submissions_broker_idx").on(table.brokerAccountId),
  ]
);

export const gaSubmissionFiles = pgTable("ga_submission_files", {
  id: varchar("id").primaryKey(),
  submissionId: varchar("submission_id").notNull().references(() => gaCaseSubmissions.id, { onDelete: "cascade" }),
  fileName: varchar("file_name", { length: 500 }).notNull(),
  fileSize: integer("file_size"),
  contentType: varchar("content_type", { length: 255 }),
  objectPath: text("object_path").notNull(),
  uploadedBy: varchar("uploaded_by", { length: 50 }).default("broker"),
  pendingItemId: varchar("pending_item_id", { length: 255 }),
  createdAt: timestamp("created_at").defaultNow(),
});

// GA Submission Pending Items — specific items UW team is waiting on from the broker
export const gaSubmissionPendingItems = pgTable("ga_submission_pending_items", {
  id: varchar("id").primaryKey(),
  submissionId: varchar("submission_id").notNull().references(() => gaCaseSubmissions.id, { onDelete: "cascade" }),
  description: text("description").notNull(),
  employeeName: varchar("employee_name", { length: 255 }),
  category: varchar("category", { length: 100 }),
  dueDate: varchar("due_date", { length: 20 }),
  // Status: pending | received | waived
  status: varchar("status", { length: 50 }).default("pending"),
  resolvedAt: timestamp("resolved_at"),
  resolvedNote: text("resolved_note"),
  createdAt: timestamp("created_at").defaultNow(),
  createdBy: varchar("created_by", { length: 255 }),
});

// GA Submission Messages — thread between GA underwriting team and broker
export const gaSubmissionMessages = pgTable("ga_submission_messages", {
  id: varchar("id").primaryKey(),
  submissionId: varchar("submission_id").notNull().references(() => gaCaseSubmissions.id, { onDelete: "cascade" }),
  senderRole: varchar("sender_role", { length: 20 }).notNull(),
  senderName: varchar("sender_name", { length: 255 }),
  message: text("message").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// GA Documents — files/resources shared by GA with their broker network
export const gaDocuments = pgTable(
  "ga_documents",
  {
    id: varchar("id").primaryKey(),
    gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    description: text("description"),
    mimeType: varchar("mime_type", { length: 100 }),
    sizeBytes: integer("size_bytes"),
    tags: text("tags").array().default([]),
    fileData: text("file_data"), // base64-encoded file content
    uploadedAt: timestamp("uploaded_at").defaultNow(),
    uploadedBy: varchar("uploaded_by", { length: 255 }),
    isActive: boolean("is_active").default(true),
  },
  (table) => [index("ga_documents_ga_id_idx").on(table.gaId)]
);

// GA Notification Preferences — per-alert-type channel settings
export const gaNotificationPrefs = pgTable(
  "ga_notification_prefs",
  {
    id: varchar("id").primaryKey(),
    gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
    alertType: varchar("alert_type", { length: 100 }).notNull(),
    emailEnabled: boolean("email_enabled").default(true),
    inAppEnabled: boolean("in_app_enabled").default(true),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [index("ga_notification_prefs_ga_id_idx").on(table.gaId)]
);

// GA Locations — physical offices/branches
export const gaLocations = pgTable("ga_locations", {
  id: varchar("id").primaryKey(),
  gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  address: varchar("address", { length: 500 }),
  city: varchar("city", { length: 255 }),
  state: varchar("state", { length: 2 }),
  zip: varchar("zip", { length: 20 }),
  phone: varchar("phone", { length: 20 }),
  email: varchar("email", { length: 255 }),
  isPrimary: boolean("is_primary").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

// GA Carrier Configs — per-carrier UW guidelines and document checklist templates
export const gaCarrierConfigs = pgTable("ga_carrier_configs", {
  id: varchar("id").primaryKey(),
  gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
  carrierName: varchar("carrier_name", { length: 255 }).notNull(),
  displayName: varchar("display_name", { length: 255 }),
  minParticipationPct: integer("min_participation_pct").default(75),
  minLives: integer("min_lives").default(2),
  uwGuidelines: text("uw_guidelines"),
  checklistItems: jsonb("checklist_items").default([]),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// GA State Licenses — per-state producer / GA licenses
export const gaStateLicenses = pgTable("ga_state_licenses", {
  id: varchar("id").primaryKey(),
  gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
  state: varchar("state", { length: 2 }).notNull(),
  licenseNumber: varchar("license_number", { length: 100 }),
  licenseType: varchar("license_type", { length: 255 }),
  status: varchar("status", { length: 20 }).notNull().default("active"),
  effectiveDate: varchar("effective_date", { length: 20 }),
  expirationDate: varchar("expiration_date", { length: 20 }),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
});

// GA Service Requests — broker-to-GA help requests (setup, service, etc.)
export const gaServiceRequests = pgTable("ga_service_requests", {
  id: serial("id").primaryKey(),
  gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
  brokerAccountId: varchar("broker_account_id").notNull(),
  brokerName: varchar("broker_name"),
  requestType: varchar("request_type").notNull(), // "setup", "onboarding", "service", "training", "technical", "other"
  title: varchar("title").notNull(),
  description: text("description").notNull(),
  priority: varchar("priority").notNull().default("normal"), // "low", "normal", "high", "urgent"
  status: varchar("status").notNull().default("open"), // "open", "in_progress", "resolved", "closed"
  gaNote: text("ga_note"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => ({
  gaIdx: index("ga_svc_req_ga_idx").on(table.gaId),
  brokerIdx: index("ga_svc_req_broker_idx").on(table.brokerAccountId),
}));

// GA Renewal Outreach — GA-initiated outreach tied to a broker's client renewal
export const gaRenewalOutreach = pgTable("ga_renewal_outreach", {
  id: serial("id").primaryKey(),
  gaId: varchar("ga_id").notNull().references(() => gaProfiles.id, { onDelete: "cascade" }),
  brokerAccountId: varchar("broker_account_id").notNull(),
  clientId: integer("client_id").notNull(),
  clientName: varchar("client_name"),
  renewalDate: timestamp("renewal_date"),
  outreachType: varchar("outreach_type").notNull(), // "quoting_support","enrollment_support","renewal_review","market_check","other"
  message: text("message"),
  status: varchar("status").notNull().default("sent"), // "sent","accepted","declined","completed"
  brokerNote: text("broker_note"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => ({
  gaIdx: index("ga_renewal_outreach_ga_idx").on(table.gaId),
  brokerIdx: index("ga_renewal_outreach_broker_idx").on(table.brokerAccountId),
  clientIdx: index("ga_renewal_outreach_client_idx").on(table.clientId),
}));

// Insert schemas
export const insertGaSubmissionPendingItemSchema = createInsertSchema(gaSubmissionPendingItems).omit({ createdAt: true, resolvedAt: true });
export const insertGaSubmissionMessageSchema = createInsertSchema(gaSubmissionMessages).omit({ createdAt: true });
export const insertGaProfileSchema = createInsertSchema(gaProfiles).omit({ createdAt: true });
export const insertGaUserSchema = createInsertSchema(gaUsers).omit({ invitedAt: true, acceptedAt: true });
export const insertGaLicenseBlockSchema = createInsertSchema(gaLicenseBlocks).omit({ purchasedAt: true });
export const insertGaBrokerLinkSchema = createInsertSchema(gaBrokerLinks).omit({ linkedAt: true });
export const insertGaBuildAccessSchema = createInsertSchema(gaBuildAccess).omit({ grantedAt: true });
export const insertGaClientVisibilitySchema = createInsertSchema(gaClientVisibility).omit({ sharedAt: true });
export const insertGaAuditLogSchema = createInsertSchema(gaAuditLogs).omit({ createdAt: true });
export const insertGaApiConnectionSchema = createInsertSchema(gaApiConnections).omit({ createdAt: true, updatedAt: true });
export const insertGaDocumentSchema = createInsertSchema(gaDocuments).omit({ uploadedAt: true });
export const insertGaNotificationPrefSchema = createInsertSchema(gaNotificationPrefs).omit({ updatedAt: true });
export const insertGaCarrierConfigSchema = createInsertSchema(gaCarrierConfigs).omit({ createdAt: true, updatedAt: true });

// Types
export type GaProfile = typeof gaProfiles.$inferSelect;
export type InsertGaProfile = z.infer<typeof insertGaProfileSchema>;
export type GaUser = typeof gaUsers.$inferSelect;
export type InsertGaUser = z.infer<typeof insertGaUserSchema>;
export type GaLicenseBlock = typeof gaLicenseBlocks.$inferSelect;
export type GaBrokerLink = typeof gaBrokerLinks.$inferSelect;
export type GaBuildAccess = typeof gaBuildAccess.$inferSelect;
export type GaClientVisibility = typeof gaClientVisibility.$inferSelect;
export type GaAuditLog = typeof gaAuditLogs.$inferSelect;
export type GaAssistedTag = typeof gaAssistedTags.$inferSelect;
export type GaApiConnection = typeof gaApiConnections.$inferSelect;
export type BrokerEngagementSnapshot = typeof brokerEngagementSnapshots.$inferSelect;
export type GaNetworkSnapshot = typeof gaNetworkSnapshots.$inferSelect;
export type GaDocument = typeof gaDocuments.$inferSelect;
export type InsertGaDocument = z.infer<typeof insertGaDocumentSchema>;
export type GaNotificationPref = typeof gaNotificationPrefs.$inferSelect;
export type InsertGaNotificationPref = z.infer<typeof insertGaNotificationPrefSchema>;
export type GaCarrierConfig = typeof gaCarrierConfigs.$inferSelect;
export type InsertGaCarrierConfig = z.infer<typeof insertGaCarrierConfigSchema>;
